import type PianoInstrument from './pianoInstrument';
import type KeySignature from './keySignature';
import { resolutionPatterns } from './resolutionPatterns';
import Music from './music';
import Note from './note';
import type { ScaleDegree } from '../uTheory.Interaction/solfegeDefinitions';

export interface ResolutionOptions {
  register?: 'treble' | 'bass' | 'auto';
  tempo?: number;
}

export class Resolution {
  constructor(private player: PianoInstrument) {}

  resolve(key: KeySignature, note: Note, options?: ResolutionOptions): Promise<void>;
  resolve(key: KeySignature, scaleDegree: ScaleDegree, options?: ResolutionOptions): Promise<void>;
  resolve(
    key: KeySignature,
    scaleDegreeOrNote: ScaleDegree | Note,
    options: ResolutionOptions = {}
  ): Promise<void> {
    let { tempo = 60, register = 'auto' } = options;

    // If register is auto, and a note is given below B3, use bass register
    if (register === 'auto' && scaleDegreeOrNote instanceof Note) {
      if (scaleDegreeOrNote.isLessThan('B3')) register = 'bass';
      else register = 'treble';
    }

    const scaleDegree =
      scaleDegreeOrNote instanceof Note
        ? key.getScaleDegreeAtNote(scaleDegreeOrNote)
        : scaleDegreeOrNote;
    if (!scaleDegree) {
      throw Error(`${scaleDegreeOrNote} is not a valid note or scale degree in ${key}`);
    }

    const qualityIndex = key.quality + (register === 'bass' ? 'Bass' : '');
    console.log({ qualityIndex });
    const pattern =
      resolutionPatterns[key.quality + (register === 'bass' ? 'Bass' : '')][scaleDegree];
    const music = Music.fromString(pattern);
    music.tempo = tempo;
    const tonic = key.tonic.clone();
    if (scaleDegreeOrNote instanceof Note) {
      const startingNoteOctaveAdjusted = scaleDegreeOrNote.clone();
      console.log({ startingNoteOctaveAdjusted });
      if (register === 'bass') startingNoteOctaveAdjusted.octave++;
      moveNoteJustBelowNote(tonic, startingNoteOctaveAdjusted);
    } else {
      tonic.octave = 4;
      if (tonic.isGreaterThan('Ab4')) tonic.octave = 3;
    }
    const c = new Note('C4');
    const interval = c.getIntervalTo(tonic);
    if (!interval) throw Error(`Cannot get interval from C to ${tonic}`);
    music.transpose(interval);
    this.player.playMusic(music);
    const midiMessages = music.getMidiMessages();

    const finalStopBeat = midiMessages[midiMessages.length - 1].startBeat + 2;
    const finalStopSecond = (finalStopBeat * 60) / tempo;

    return new Promise((resolve) => {
      this.player.play('silence-2', { startTime: finalStopSecond }).onEnded(resolve);
    });
  }
}

function moveNoteJustBelowNote(noteToMove: Note, referenceNote: Note) {
  noteToMove.octave = referenceNote.octave;
  if (noteToMove.isGreaterThan(referenceNote)) {
    noteToMove.octave--;
  }
  return noteToMove;
}
